// CANVAS-VOICE: the Live page's voice panel. The person picks who they talk to (a DE with a seat)
// and where its new cards go (a board they can see), then starts the session: voice when every
// provider key is set (the LiveKit room joins with the microphone on), otherwise text in this
// same panel, with the notice naming what is missing ("voice not configured: … missing"). What
// the front shows lands on this Live session's canvas over the live channel.
import { Button, Input, NativeSelect, NativeSelectOption, Notice } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { MicIcon, SendIcon, SquareIcon } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import {
  mergeCommands,
  readVoiceStatus,
  startVoiceSession,
  type VoiceCommand,
  type VoiceCommandStatus,
  type VoiceSession,
  VoiceStartError,
} from "./voice";

type Line = { who: "you" | "voice"; text: string };

/** LANE-C: how often the command list re-reads its status while a session is open. */
export const COMMANDS_POLL_MS = 5_000;

const STATUS_LABEL: Record<VoiceCommandStatus, string> = {
  accepted: "Accepted",
  queued: "Queued",
  applied: "Applied",
  failed: "Failed",
  awaiting_confirmation: "Waiting for your yes",
  cancelled: "Cancelled",
};

/** The status a command shows: a steer names where it went, a request names its state. */
export function commandLabel(c: VoiceCommand): string {
  if (c.request?.stuck) return "Stuck: needs you";
  if (c.request?.state === "delivered") return "Delivered for review";
  if (c.request?.state === "accepted") return "Accepted by you";
  if (c.kind === "steer" && c.delivery === "running_turn")
    return "Applied: reached the running turn";
  if (c.kind === "steer" && (c.delivery === "next_turn" || c.status === "queued"))
    return "Queued for the next turn";
  return STATUS_LABEL[c.status];
}

/** LANE-C: what the voice front did in this session, one row per command, newest last. */
export function VoiceCommands({ commands }: { commands: VoiceCommand[] }) {
  if (commands.length === 0) return null;
  return (
    <ul aria-label="Commands" data-slot="voice-commands" className="flex flex-col gap-1 text-xs">
      {commands.map((c) => (
        <li key={c.key} data-status={c.status} className="flex items-baseline gap-2">
          <span className="shrink-0 rounded border px-1.5 py-0.5 font-medium">
            {commandLabel(c)}
          </span>
          <span className="min-w-0 truncate text-muted-foreground">{c.detail}</span>
        </li>
      ))}
    </ul>
  );
}

/** Whether this document's Permissions-Policy allows the microphone (true where unknown). */
function micAllowedHere(): boolean {
  const p = (
    document as Document & { permissionsPolicy?: { allowsFeature?: (f: string) => boolean } }
  ).permissionsPolicy;
  return p?.allowsFeature ? p.allowsFeature("microphone") : true;
}

/**
 * The DEs with a seat (the ones a person can talk to on Live). LIVE-WORKSPACE: shared by the voice
 * panel and the docked chat, so both talk to the same DE (one cache entry).
 */
export function useSeatedDes(api: Api, co: string) {
  const des = useQuery({
    queryKey: ["voice-panel", co, "des"],
    queryFn: () => api.client.digitalEmployees.list({ params: { co }, query: { limit: 100 } }),
  });
  const seated = (des.data?.data ?? []).filter((d) => d.seat !== null);
  return { des, seated };
}

export function VoicePanel({
  api,
  company,
  sessionId,
  onClose,
  deId: chosenDe,
  onDeChange,
  focusTaskId,
}: {
  api: Api;
  company: CompanyAccess;
  sessionId: string;
  onClose: () => void;
  /** LANE-C: the card the canvas is on (its chain block): the target typed words keep. */
  focusTaskId?: string | null | undefined;
  /** LIVE-WORKSPACE: the DE picked on the page (the docked chat's); absent: the panel's own. */
  deId?: string | undefined;
  onDeChange?: ((id: string) => void) | undefined;
}) {
  const co = company.id;
  const { des, seated } = useSeatedDes(api, co);
  const boards = useQuery({
    queryKey: ["voice-panel", co, "boards"],
    queryFn: () => api.client.boards.list({ params: { co }, query: { limit: 200 } }),
  });
  // LIVE-WORKSPACE: said before Start — "voice not configured: … missing" or not reachable.
  const status = useQuery({
    queryKey: ["voice-panel", "status"],
    queryFn: () => readVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const notSetUp = !!status.data && status.data.voice !== "configured";
  const [ownDe, setOwnDe] = useState("");
  const deId = chosenDe ?? ownDe;
  const setDeId = onDeChange ?? setOwnDe;
  const [boardId, setBoardId] = useState("");
  const de = seated.find((d) => d.membership_id === deId) ?? seated[0] ?? null;
  const board =
    (boards.data?.data ?? []).find((b) => b.id === boardId) ?? boards.data?.data[0] ?? null;

  const [session, setSession] = useState<VoiceSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [text, setText] = useState("");
  const [commands, setCommands] = useState<VoiceCommand[]>([]);
  const [focus, setFocus] = useState<string | null>(null);
  const live = useRef<VoiceSession | null>(null);
  useEffect(() => () => live.current?.stop(), []);
  // Statuses move after the turn (the DE starts, delivers, gets stuck): re-read them.
  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => {
      void session
        .refresh()
        .then((s) => {
          setCommands((l) => mergeCommands(l, s.commands));
          if (s.focus) setFocus(s.focus.id);
        })
        .catch(() => undefined);
    }, COMMANDS_POLL_MS);
    return () => clearInterval(t);
  }, [session]);

  const start = async () => {
    if (!de?.seat || !board) return;
    setBusy(true);
    setError(null);
    try {
      const s = await startVoiceSession({
        companyId: co,
        sessionId,
        deMembershipId: de.membership_id,
        deSeatId: de.seat.id,
        deName: de.display_name,
        boardId: board.id,
      });
      live.current = s;
      setSession(s);
      // A resumed session (reload, reconnect) shows what it already did, never redoes it.
      setCommands(s.state.commands);
      setFocus(s.state.focus?.id ?? null);
    } catch (e) {
      setError(e instanceof VoiceStartError ? e.message : "Voice could not start.");
    } finally {
      setBusy(false);
    }
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const said = text.trim();
    if (!session || !said) return;
    setText("");
    setLines((l) => [...l, { who: "you", text: said }]);
    setBusy(true);
    try {
      // Typing keeps the target: the session's focus, else the card the canvas is on.
      const t = await session.say(said, focus ?? focusTaskId ?? null);
      if (t.reply) setLines((l) => [...l, { who: "voice", text: t.reply }]);
      if (t.commands?.length) setCommands((l) => mergeCommands(l, t.commands ?? []));
      if (t.focus) setFocus(t.focus.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not go through.");
    } finally {
      setBusy(false);
    }
  };

  const stop = () => {
    live.current?.stop();
    live.current = null;
    setSession(null);
    onClose();
  };

  return (
    <section
      aria-label="Voice"
      data-slot="voice-panel"
      className="flex flex-col gap-2 rounded-lg border p-3 text-sm"
    >
      {error ? (
        <Notice tone="danger" icon={null} role="alert" className="py-1 text-xs">
          {error}
        </Notice>
      ) : null}
      {!session && notSetUp ? (
        <Notice tone="neutral" icon={null} role="status" className="py-1 text-xs">
          Voice isn't set up for this workspace yet. You can type instead.
          {isManager(company) && status.data && status.data.missing.length > 0 ? (
            <details className="mt-1">
              <summary className="cursor-pointer">Setup details</summary>
              <ul className="mt-1 list-disc pl-4">
                {status.data.missing.map((name) => (
                  <li key={name}>{name} is missing</li>
                ))}
              </ul>
            </details>
          ) : null}
        </Notice>
      ) : null}
      {!session && status.data?.voice === "configured" && !micAllowedHere() ? (
        // The microphone is allowed on the Live document only (Caddy's Permissions-Policy): a
        // page reached by in-app navigation carries another page's policy until it reloads.
        <Notice tone="neutral" icon={null} role="status" className="py-1 text-xs">
          Reload this page to let voice use the microphone.{" "}
          <Button size="sm" variant="link" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Notice>
      ) : null}
      {!session && status.isError ? (
        <Notice tone="neutral" icon={null} role="status" className="py-1 text-xs">
          Voice not configured: the voice service is not reachable. Type in the chat beside the
          workspace instead.
        </Notice>
      ) : null}
      {!session ? (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect
            size="sm"
            aria-label="Talk to"
            value={de?.membership_id ?? ""}
            onChange={(e) => setDeId(e.target.value)}
          >
            {seated.map((d) => (
              <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                {d.display_name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            size="sm"
            aria-label="New cards go to"
            value={board?.id ?? ""}
            onChange={(e) => setBoardId(e.target.value)}
          >
            {(boards.data?.data ?? []).map((b) => (
              <NativeSelectOption key={b.id} value={b.id}>
                {b.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            size="sm"
            disabled={busy || !de || !board || notSetUp}
            onClick={() => void start()}
          >
            <MicIcon aria-hidden="true" />
            Start
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
          {des.isSuccess && seated.length === 0 ? (
            <span className="text-muted-foreground">No digital employee with a seat here.</span>
          ) : null}
        </div>
      ) : (
        <>
          {session.notice ? (
            <Notice tone="neutral" icon={null} role="status" className="py-1 text-xs">
              {session.notice}
            </Notice>
          ) : (
            <p className="text-muted-foreground" role="status">
              Voice is on: talk to {de?.display_name ?? "your DE"}.
            </p>
          )}
          <p className="text-xs text-muted-foreground" data-slot="voice-barge-in">
            Talking over the reply stops the reply, not the work: a card or correction already
            handed over keeps going. Saying the same words again shows what was already done; say
            "again" to repeat on purpose.
          </p>
          <VoiceCommands commands={commands} />
          <ol className="flex max-h-64 flex-col gap-1 overflow-y-auto" aria-live="polite">
            {lines.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <li key={i} className={l.who === "you" ? "text-foreground" : "text-muted-foreground"}>
                <span className="font-medium">{l.who === "you" ? "You" : "Voice"}:</span> {l.text}
              </li>
            ))}
          </ol>
          <form className="flex items-center gap-2" onSubmit={(e) => void send(e)}>
            <Input
              aria-label="Say"
              placeholder={`Try "hello" or "show me today's board"`}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button size="sm" type="submit" disabled={busy || !text.trim()}>
              <SendIcon aria-hidden="true" />
              Send
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={stop}>
              <SquareIcon aria-hidden="true" />
              End
            </Button>
          </form>
        </>
      )}
    </section>
  );
}
